import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Button from "../button";
import BaseModal from "../modal/base/BaseModal";
import { useJoinTermsList } from "../../../@apis/hooks/useAuth";
import { useModalStore } from "../../../lib/store/useModalStore";
import { getMyInfo, updateMarketingAgree } from "../../../@apis/v1/auth.service";
import { getQueryClient } from "../../../@apis/queryClient";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";
import { useMarketingTermsAgreeStore } from "../../../lib/store/useMarketingTermsAgreeStore";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";

type MarketingAgreementDialogProps = {
  close: () => void;
  onAgree?: () => void;
  onLater?: () => void;
};

const MarketingAgreementDialog = ({
  close,
  onAgree,
  onLater,
}: MarketingAgreementDialogProps) => {
  const { openModal } = useModalStore();
  const { setMyInfo } = useMyInfoStore();
  const { data: termsData } = useQuery({ ...useJoinTermsList() });
  const pendingTermsAgreeId = useMarketingTermsAgreeStore(
    (s) => s.pendingAgreeId,
  );
  const clearPendingTermsAgree = useMarketingTermsAgreeStore((s) => s.clear);
  const marketingTerm = termsData?.data?.termsList.find(
    ({ type }) => type === "marketing",
  );
  const updateMarketingAgreeMutation = useMutation({
    mutationFn: updateMarketingAgree,
    onSuccess: async (res, variables) => {
      if (res.success) {
        // 서버 최신 값으로 캐시·스토어 동기화 (마이페이지 진입 시 stale 캐시로 팝업 재노출되는 문제 방지)
        const me = await getQueryClient().fetchQuery({
          queryKey: ["auth-my-info"],
          queryFn: getMyInfo,
        });
        if (me.data) setMyInfo(me.data);

        if (variables.agreed) {
          openModal("ALERT_DIALOG", {
            title: "알림",
            content: "마케팅 정보 수신·활용에\n동의 되었습니다.",
            onConfirm: () => {
              onAgree?.();
              close();
            },
          });
        } else {
          openModal("ALERT_DIALOG", {
            title: "알림",
            content: "마케팅 정보 수신·활용에\n거부 되었습니다.",
            onConfirm: () => {
              onLater?.();
              close();
            },
          });
        }
      } else {
        openModal("ALERT_DIALOG", {
          title: "에러",
          content: res.error?.message ?? "마케팅 동의 처리에 실패했습니다.",
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "에러",
        content: error?.message ?? "마케팅 동의 처리에 실패했습니다.",
      });
    },
  });

  const handleLater = () => {
    if (!marketingTerm?.id) {
      onLater?.();
      close();
      return;
    }

    updateMarketingAgreeMutation.mutate({
      termsId: marketingTerm.id,
      agreed: false,
    });
  };

  const handleAgree = () => {
    if (!marketingTerm?.id) return;

    updateMarketingAgreeMutation.mutate({
      termsId: marketingTerm.id,
      agreed: true,
    });
  };

  React.useEffect(() => {
    if (pendingTermsAgreeId == null) return;

    updateMarketingAgreeMutation.mutate({
      termsId: pendingTermsAgreeId,
      agreed: true,
    });
    clearPendingTermsAgree();
  }, [clearPendingTermsAgree, pendingTermsAgreeId]);

  const handleOpenTerms = () => {
    openModal("TERMS_MODAL", {
      html: marketingTerm?.term ?? "",
      type: "marketing",
      agreeTermsId: marketingTerm?.id,
      agreeChannel: "marketing",
    });
  };

  return (
    <BaseModal
      className="p-[20px] rounded-none min-w-[311px]"
      size="dialog"
      close={handleLater}
      enableOutsideClick={false}
    >
      <section className="flex flex-col gap-y-[20px] ">
        <div className="flex-1 space-y-[10px] flex flex-col items-center">
          {/* 이미지 */}
          <img src="/icons/ic-gift.png" className="size-[32px]" />
          {/* 제목 */}
          <h1 className="text-base font-semibold text-black break-keep whitespace-pre-wrap text-center mx-[44px]">
            파크골프장 예약 소식과 대회 정보를 놓치지 마세요!
          </h1>
          {/* 콘텐츠 */}
          <div className="p-[10px] bg-[#f2f2f7] space-y-[10px]">
            <p className="text-[#8e8e93] text-xs">
              &apos;동의하고 소식받기&apos;를 누르면 마케팅 정보 수신·활용에
              동의하게 됩니다. &apos;마이페이지 &gt; 개인정보 동의
              설정&apos;에서 언제든지 다시 변경하실 수 있습니다.
            </p>
            <button
              type="button"
              className="underline text-black text-xs text-left"
              onClick={handleOpenTerms}
            >
              마케팅 활용 동의 약관 보기 →
            </button>
          </div>
        </div>
        <div className="flex-row-between gap-[10px] shrink-0">
          <Button
            className="w-full text-base px-[3px] py-[12px]"
            label="다음에 할게요"
            variant={"white"}
            disabled={updateMarketingAgreeMutation.isPending}
            onClick={handleLater}
          />
          <Button
            className="w-full text-base px-[3px] py-[12px]"
            label="동의하고 소식받기"
            variant={
              !marketingTerm?.id || updateMarketingAgreeMutation.isPending
                ? "disabled"
                : "black"
            }
            disabled={!marketingTerm?.id || updateMarketingAgreeMutation.isPending}
            onClick={handleAgree}
          />
        </div>
      </section>
    </BaseModal>
  );
};

export default React.memo(MarketingAgreementDialog);
